import axios from "axios";
import authStorage from "../utils/authStorage";

const API_URL = process.env.VUE_APP_ROOT_API;

// -------------------------
// Singleton refresh promise
// -------------------------
let refreshPromise = null;

// -------------------------
// Logout callback (set by app)
// -------------------------
let logoutCallback = null;

// -------------------------
// Refresh token function
// -------------------------
const refreshAccessToken = async () => {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
    if (!response.data?.token) return false;
    authStorage.setTokens(response.data);
    return true;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return false;
  }
};

// -------------------------
// Handle 401 — singleton refresh with retry
// -------------------------
const handleUnauthorized = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        return await refreshAccessToken();
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  const refreshed = await refreshPromise;

  if (!refreshed) {
    authStorage.clearSession();
    logoutCallback?.();
    throw new Error("Unauthorized");
  }

  return true;
};

// -------------------------
// Build request config with auth headers
// -------------------------
const buildConfig = (options = {}) => {
  const authHeaders = authStorage.getAuthHeaders();
  return {
    ...options,
    headers: {
      ...(authHeaders.headers || {}),
      ...(options.headers || {})
    }
  };
};

// -------------------------
// Main request function with auto-refresh on 401
// -------------------------
const request = async (method, url, data = null, options = {}, _retried = false) => {
  const fullUrl = API_URL + url;
  const config = buildConfig(options);

  const execute = () => {
    switch (method.toLowerCase()) {
      case "get":    return axios.get(fullUrl, config);
      case "post":   return axios.post(fullUrl, data, config);
      case "put":    return axios.put(fullUrl, data, config);
      case "patch":  return axios.patch(fullUrl, data, config);
      case "delete": return axios.delete(fullUrl, config);
      default:       return axios.request({ ...config, method, url: fullUrl, data });
    }
  };

  try {
    return await execute();
  } catch (error) {
    if (error.response?.status !== 401) throw error;
    if (_retried) {
      authStorage.clearSession();
      logoutCallback?.();
      throw new Error("Unauthorized");
    }
    await handleUnauthorized();
    return request(method, url, data, options, true);
  }
};

// -------------------------
// Public API
// -------------------------
const ApiService = {
  setLogoutCallback(fn) {
    logoutCallback = fn;
  },

  get(url, options = {}) {
    return request("get", url, null, options);
  },

  post(url, data = {}, options = {}) {
    return request("post", url, data, options);
  },

  put(url, data = {}, options = {}) {
    return request("put", url, data, options);
  },

  patch(url, data = {}, options = {}) {
    return request("patch", url, data, options);
  },

  delete(url, options = {}) {
    return request("delete", url, null, options);
  }
};

export default ApiService;
